import { Amplify } from 'aws-amplify';
import { signIn, signUp, signOut, confirmSignUp, resendSignUpCode, getCurrentUser, signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';
import { logger } from './logger';

// AWS Cognito 配置
// 使用不带客户端密钥的应用客户端
export const configureAmplify = (config: any) => {
  logger.log('配置AWS Amplify:', {
    hasUserPoolId: !!config.USER_POOL_ID,
    hasClientId: !!config.USER_POOL_WEB_CLIENT_ID,
    hasOAuthDomain: !!config.OAUTH_DOMAIN
  });
  
  // 只配置 Amplify 需要的参数，不包含客户端密钥
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.USER_POOL_ID,
        userPoolClientId: config.USER_POOL_WEB_CLIENT_ID,
        // 暂时禁用OAuth配置，使用自定义处理
        // loginWith: {
        //   oauth: {
        //     domain: config.OAUTH_DOMAIN,
        //     scopes: ['openid'],
        //     redirectSignIn: [window.location.origin + '/'],
        //     redirectSignOut: [window.location.origin + '/'],
        //     responseType: 'code'
        //   }
        // }
      }
    }
  });
};

// 检查用户是否已登录
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const session = await fetchAuthSession();
    return !!session.tokens;
  } catch (error) {
    return false;
  }
};

// 获取当前用户信息
export const getCurrentUserInfo = async () => {
  try {
    return await getCurrentUser();
  } catch (error) {
    return null;
  }
};

// 邮箱注册
export const signUpUser = async (email: string, password: string) => {
  try {
    logger.log('=== 开始注册用户 ===');
    logger.log('邮箱:', email);
    logger.log('AWS Amplify配置:', Amplify.getConfig());
    
    // 检查配置
    const config = Amplify.getConfig();
    const clientId = config.Auth?.Cognito?.userPoolClientId;
    logger.log('客户端ID:', clientId);
    
    if (!clientId) {
      logger.error('错误：未找到客户端ID');
      return { success: false, error: 'AWS配置错误：未找到客户端ID' };
    }
    
    logger.log('准备调用signUp函数...');
    
    const result = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email
        }
      }
    });
    
    logger.log('=== 注册成功 ===');
    logger.log('注册结果:', result);
    return { success: true, result };
  } catch (error: any) {
    logger.error('=== 注册失败 ===');
    logger.error('错误对象:', error);
    logger.error('错误详情:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    
    
    return { success: false, error: error.message || '注册失败，请检查网络连接' };
  }
};

// 邮箱登录
export const signInUser = async (email: string, password: string) => {
  try {
    logger.log('开始登录用户:', email);
    
    // 尝试使用AWS Amplify的signIn，但配置为使用正确的认证流程
    const result = await signIn({
      username: email,
      password,
      options: {
        authFlowType: 'USER_SRP_AUTH'
      }
    });
    
    logger.log('登录结果:', result);
    return { success: true, result };
  } catch (error: any) {
    logger.error('登录错误:', error);
    logger.error('错误详情:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    
    return { success: false, error: error.message || '登录失败' };
  }
};

// 谷歌登录 - 基本版本
export const signInWithGoogle = async () => {
  try {
    logger.log('开始Google登录...');
    logger.log('AWS Amplify配置:', Amplify.getConfig());
    
    await signInWithRedirect({ provider: 'Google' });
    logger.log('Google登录重定向已启动');
    return { success: true };
  } catch (error: any) {
    logger.error('Google登录失败:', error);
    logger.error('错误详情:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    return { success: false, error: error.message || 'Google登录失败' };
  }
};

// 谷歌登录 - 不使用provider参数
export const signInWithGoogleBasic = async () => {
  try {
    logger.log('开始基本Google登录...');
    logger.log('AWS Amplify配置:', Amplify.getConfig());
    
    await signInWithRedirect();
    logger.log('基本OAuth登录重定向已启动');
    return { success: true };
  } catch (error: any) {
    logger.error('基本Google登录失败:', error);
    logger.error('错误详情:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    return { success: false, error: error.message || '基本Google登录失败' };
  }
};

// 登出
export const signOutUser = async () => {
  try {
    // 使用全局登出，不重定向到Cognito
    await signOut({ global: false });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// 确认注册验证码
export const confirmSignUpUser = async (email: string, code: string) => {
  try {
    const result = await confirmSignUp({
      username: email,
      confirmationCode: code
    });
    return { success: true, result };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// 重新发送验证码
export const resendSignUpUser = async (email: string) => {
  try {
    const result = await resendSignUpCode({
      username: email
    });
    return { success: true, result };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// 处理OAuth回调
export const handleOAuthCallback = async () => {
  try {
    const session = await fetchAuthSession();
    return { success: true, session };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
